"use client"

import { useState, useEffect, useCallback } from "react"
import { format } from "date-fns"
import type { DateRange } from "react-day-picker"
import {
  CalendarIcon,
  DownloadIcon,
  Loader2,
  SendIcon,
  SearchIcon,
  XCircleIcon,
} from "lucide-react"
import { DashboardHeader } from "@/components/dashboard-header"
import { DashboardShell } from "@/components/dashboard-shell"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { AppointmentList } from "@/components/appointment-list"
import { AppointmentFormModal } from "@/components/appointment-form-modal"
import { useToast } from "@/components/ui/use-toast"
import { Appointment, Doctor, Patient, AppointmentFormData } from "@/types"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { supabase } from "@/utils/supabase/client"
import { useOrganization } from "@/contexts/organization-context"


const statusOptions = ["Scheduled", "Completed", "Cancelled", "Rescheduled"];

export default function AppointmentsPage() {
  const { organization } = useOrganization()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()

  // Filtering states
  const [searchQuery, setSearchQuery] = useState("")
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [patients, setPatients] = useState<Patient[]>([])
  const [selectedDoctor, setSelectedDoctor] = useState("all")
  const [dateRange, setDateRange] = useState<DateRange | undefined>()
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(statusOptions)

  const fetchDoctors = async () => {
    try {
      const { data, error } = await supabase.from("doctors").select("*")
      if (error) throw error
      setDoctors(data || [])
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not fetch doctors list.",
        variant: "destructive",
      })
    }
  }

  const fetchPatients = async () => {
    try {
      const { data, error } = await supabase.from("patients").select("*")
      if (error) throw error
      setPatients(data || [])
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not fetch patients list.",
        variant: "destructive",
      })
    }
  }

  const fetchAppointments = useCallback(async () => {
    if (!organization) return;
    setIsLoading(true)
    try {
        let query = supabase
            .from('appointments')
            .select(
                `
                *,
                patients (*),
                doctors (*)
            `
            )
            .eq('organization_id', organization.id)
            .order('appointment_date', { ascending: false });

      if (searchQuery) {
        query = query.ilike('patients.name', `%${searchQuery}%`);
      }
      if (selectedDoctor !== "all") {
        query = query.eq('doctor_id', selectedDoctor)
      }
      if (dateRange?.from) {
        query = query.gte('appointment_date', format(dateRange.from, "yyyy-MM-dd"))
      }
      if (dateRange?.to) {
        query = query.lte('appointment_date', format(dateRange.to, "yyyy-MM-dd"))
      }
      if (selectedStatuses.length > 0 && selectedStatuses.length < statusOptions.length) {
        query = query.in('status', selectedStatuses)
      }

      const { data, error } = await query;
      
      if (error) throw error
      setAppointments(data as any || [])
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load appointments: ${error.message}`,
        variant: "destructive",
      })
    } finally {
    setIsLoading(false)
  }
  }, [searchQuery, selectedDoctor, dateRange, selectedStatuses, toast, organization])

  useEffect(() => {
    fetchDoctors()
    fetchPatients()
  }, [])

  useEffect(() => {
    fetchAppointments()
  }, [fetchAppointments])

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    )
  }

    const handleSaveAppointment = async (appointmentData: any) => {
    const isEditing = !!editingAppointment

    try {
            const payload = {
                patient_id: appointmentData.patient?.id,
                doctor_id: appointmentData.doctor?.id,
                appointment_date: appointmentData.appointment_date,
                appointment_time: appointmentData.appointment_time,
                status: appointmentData.status || "Scheduled",
                appointment_type: appointmentData.appointment_type,
                organization_id: organization?.id
            };

            let response;
            if (isEditing) {
                response = await supabase.from('appointments').update(payload).eq('id', editingAppointment.id);
            } else {
                response = await supabase.from('appointments').insert([payload]);
            }
      
      if (response.error) throw response.error

      fetchAppointments()

      setIsModalOpen(false)
      setEditingAppointment(null)

      toast({
        title: `Appointment ${isEditing ? "updated" : "created"}`,
        description: `Appointment for ${appointmentData.patient?.name} has been scheduled.`,
      })
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} appointment: ${error.message}`,
        variant: "destructive",
      })
    }
  }

  const handleEditAppointment = (appointment: Appointment) => {
    setEditingAppointment(appointment)
    setIsModalOpen(true)
  }

  const handleDeleteAppointment = async (id: string) => {
    try {
      const { error } = await supabase.from('appointments').delete().eq('id', id);

      if (error) throw error

      fetchAppointments() // Refetch

      toast({
        title: "Appointment deleted",
        description: "The appointment has been successfully deleted.",
      })
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete appointment: ${error.message}`,
        variant: "destructive",
      })
    }
  }

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase.from('appointments').update({ status: newStatus }).eq('id', id)

      if (error) throw error

      fetchAppointments() // Refetch

      const appointment = appointments.find((app) => app.id === id)
      toast({
        title: "Status updated",
        description: `Appointment for ${appointment!.patients.name} is now ${newStatus}.`,
      })
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to update appointment status: ${error.message}`,
        variant: "destructive",
      })
    }
  }

  const handleCancelToday = () => {
    toast({ title: "Action not implemented", description: "This feature is not yet available." })
  }

  return (
    <div className="md:ml-64">
      <DashboardShell>
        <DashboardHeader heading="Appointments" text="Manage your clinic's appointments">
          <Button
            onClick={() => {
              setEditingAppointment(null)
              setIsModalOpen(true)
            }}
          >
            Create Appointment
          </Button>
        </DashboardHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search by patient name or phone..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
            <SelectTrigger>
              <SelectValue placeholder="Select Doctor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Doctors</SelectItem>
              {doctors.map((doctor) => (
                <SelectItem key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="date"
                variant={"outline"}
                className={cn(
                  "justify-start text-left font-normal",
                  !dateRange && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "LLL dd, y")} -{" "}
                      {format(dateRange.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(dateRange.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="font-medium text-sm">Status:</span>
          {statusOptions.map((status) => (
            <Button
              key={status}
              variant={selectedStatuses.includes(status) ? "default" : "outline"}
              size="sm"
              onClick={() => handleStatusToggle(status)}
            >
              {status}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <AppointmentList
            appointments={appointments}
            onEdit={handleEditAppointment}
            onDelete={handleDeleteAppointment}
            onStatusChange={handleStatusChange}
          />
        )}

        <AppointmentFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveAppointment}
          appointmentToEdit={editingAppointment}
          patients={patients}
          doctors={doctors}
        />
      </DashboardShell>
    </div>
  )
} 